import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import { sameOriginUrl } from '@/lib/sameOriginUrl';

// Model-authored text rendered as markdown.
//
// Language models answer in markdown whether or not anything asks them to —
// headings, bullets, tables, `code`. Rendered with `whitespace-pre-wrap`, that
// arrives as literal asterisks and pipe characters, and a table becomes an
// unreadable row of punctuation. This is the one place that decision is made,
// so chat answers, bank-run results, and anything else showing model output
// stay consistent.
//
// `prose-sm` rather than the docs pages' full `prose`: this text sits inside
// cards beside UI chrome at 14px, so the documentation scale would tower over
// its own container. GFM is on for tables and strikethrough.
//
// Safety: react-markdown does not render raw HTML unless `rehype-raw` is added,
// and it is deliberately not. Two more things are refused here, because the
// text is model output and the model reads whatever the store holds: an image,
// which the browser would fetch from any address the markdown names and so
// carry data out with it; and a link to another origin, which a reader would
// follow. Links stay within this instance. Do not add raw-HTML support here
// without a sanitizer, and do not re-admit images.
const DISALLOWED_ELEMENTS = ['img'];

// A link whose address was refused keeps its words and loses the link, so the
// reader sees what the model wrote without anywhere to go.
const COMPONENTS = {
  a: ({ href, children }: { href?: string | undefined; children?: ReactNode | undefined }) =>
    href ? <a href={href}>{children}</a> : <span>{children}</span>,
};

export function Markdown({ children, className }: { children: string; className?: string }) {
  return (
    <div
      className={cn(
        'prose prose-sm prose-slate max-w-none dark:prose-invert',
        // Chat answers are short; the default vertical rhythm leaves a gap
        // above the first line that reads as a broken layout inside a card.
        'prose-p:my-2 prose-headings:mt-3 prose-headings:mb-1 prose-ul:my-2 prose-ol:my-2',
        'prose-pre:my-2 prose-table:my-2',
        'prose-a:text-tres-action',
        // The typography plugin adds decorative backticks around inline code.
        'prose-code:before:content-none prose-code:after:content-none',
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        disallowedElements={DISALLOWED_ELEMENTS}
        urlTransform={sameOriginUrl}
        components={COMPONENTS}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
